import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { fDateTime } from '@/utils/format-date'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent } from '@/components/ui/card'
import { Eye, MoreHorizontal, Trash2, Video } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import type { Trend, TrendPrivacy, TrendStatus } from './types'

type Props = {
  trends: Trend[]
  loading: boolean
  selectedIds: string[]
  onSelectedIdsChange: (ids: string[]) => void
  onView: (trend: Trend) => void
  onDelete: (trend: Trend) => void
  onTogglePublic: (trend: Trend, isPublic: boolean) => void
}

const statusLabels: Record<TrendStatus, string> = {
  active: 'พร้อมใช้งาน',
  processing: 'กำลังประมวลผล',
  failed: 'ล้มเหลว',
  hidden: 'ซ่อน',
  deleted: 'ลบแล้ว',
  banned: 'ถูกแบน',
}

const privacyLabels: Record<TrendPrivacy, string> = {
  public: 'สาธารณะ',
  friends: 'เพื่อน',
  private: 'ส่วนตัว',
}

function StatusBadge({ status }: { status: TrendStatus }) {
  if (status === 'processing') {
    return (
      <Badge className="text-xs bg-yellow-100 text-yellow-800 border-yellow-200">
        {statusLabels[status]}
      </Badge>
    )
  }
  if (status === 'failed') {
    return (
      <Badge className="text-xs bg-red-100 text-red-800 border-red-200">
        {statusLabels[status]}
      </Badge>
    )
  }
  if (status === 'deleted' || status === 'banned') {
    return (
      <Badge className="text-xs bg-gray-100 text-gray-800 border-gray-200">
        {statusLabels[status]}
      </Badge>
    )
  }
  if (status === 'hidden') {
    return (
      <Badge className="text-xs bg-slate-100 text-slate-800 border-slate-200">
        {statusLabels[status]}
      </Badge>
    )
  }
  return (
    <Badge className="text-xs bg-green-100 text-green-800 border-green-200">
      {statusLabels[status]}
    </Badge>
  )
}

function getUserName(trend: Trend) {
  return (
    trend.user?.displayName ||
    [trend.user?.firstName, trend.user?.lastName].filter(Boolean).join(' ') ||
    trend.user?.uid ||
    '-'
  )
}

export default function TrendsTable({
  trends,
  loading,
  selectedIds,
  onSelectedIdsChange,
  onView,
  onDelete,
  onTogglePublic,
}: Props) {
  const visibleIds = trends.map((trend) => trend.id)
  const selectedVisibleCount = visibleIds.filter((id) =>
    selectedIds.includes(id),
  ).length
  const allVisibleSelected =
    visibleIds.length > 0 && selectedVisibleCount === visibleIds.length

  const toggleAllVisible = (checked: boolean) => {
    if (checked) {
      onSelectedIdsChange([...new Set([...selectedIds, ...visibleIds])])
      return
    }

    onSelectedIdsChange(
      selectedIds.filter((id) => !visibleIds.includes(id)),
    )
  }

  const toggleOne = (id: string, checked: boolean) => {
    if (checked) {
      onSelectedIdsChange([...selectedIds, id])
      return
    }
    onSelectedIdsChange(selectedIds.filter((selectedId) => selectedId !== id))
  }

  if (loading) {
    return (
      <Card className="p-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead className="w-24">ภาพปก</TableHead>
                <TableHead>รายละเอียด</TableHead>
                <TableHead className="w-40">ผู้โพสต์</TableHead>
                <TableHead className="w-32">สถานะ</TableHead>
                <TableHead className="w-28">สาธารณะ</TableHead>
                <TableHead className="w-32">ยอดรวม</TableHead>
                <TableHead className="w-40">วันที่สร้าง</TableHead>
                <TableHead className="w-16">จัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 8 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="w-16 h-24 bg-gray-200 rounded-lg animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 w-2/3 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-24 bg-gray-200 rounded-full animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-12 bg-gray-200 rounded-full animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }

  if (trends.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <Video className="mx-auto mb-3 h-10 w-10 text-gray-300" />
        <p className="text-base text-muted-foreground">
          ไม่พบคลิปเทรนด์ตามตัวกรอง
        </p>
      </div>
    )
  }

  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allVisibleSelected}
                  aria-label="เลือกคลิปทั้งหมดในหน้านี้"
                  onCheckedChange={(checked) =>
                    toggleAllVisible(Boolean(checked))
                  }
                />
              </TableHead>
              <TableHead className="w-24">ภาพปก</TableHead>
              <TableHead>รายละเอียด</TableHead>
              <TableHead className="w-40">ผู้โพสต์</TableHead>
              <TableHead className="w-32">สถานะ</TableHead>
              <TableHead className="w-28">สาธารณะ</TableHead>
              <TableHead className="w-32">ยอดรวม</TableHead>
              <TableHead className="w-40">วันที่สร้าง</TableHead>
              <TableHead className="w-16">จัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trends.map((trend) => (
              <TableRow key={trend.id}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.includes(trend.id)}
                    aria-label={`เลือกคลิป ${trend.id}`}
                    onCheckedChange={(checked) =>
                      toggleOne(trend.id, Boolean(checked))
                    }
                  />
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    className="w-16 h-24 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center"
                    onClick={() => onView(trend)}
                  >
                    {trend.thumbnailUrl ? (
                      <img
                        src={trend.thumbnailUrl}
                        alt={trend.caption || trend.id}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Video className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium text-black line-clamp-2 max-w-md">
                      {trend.caption || '(ไม่มี caption)'}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {(trend.hashtags || []).slice(0, 4).map((tag) => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className="text-xs px-1.5 py-0"
                        >
                          #{tag.replace(/^#/, '')}
                        </Badge>
                      ))}
                    </div>
                    {trend.processingError && (
                      <p className="text-xs text-destructive line-clamp-1">
                        {trend.processingError}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium text-black line-clamp-1">
                      {getUserName(trend)}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {trend.user?.uid}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-2">
                    <StatusBadge status={trend.status} />
                    {trend.privacy && (
                      <Badge variant="outline" className="w-fit text-xs">
                        {privacyLabels[trend.privacy] ?? trend.privacy}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Switch
                      checked={trend.privacy === 'public'}
                      disabled={trend.status === 'deleted'}
                      onCheckedChange={(checked) =>
                        onTogglePublic(trend, checked)
                      }
                    />
                    <span className="text-xs text-muted-foreground">
                      {trend.privacy === 'public' ? 'Public' : 'Private'}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-xs text-muted-foreground">
                    <p>{trend.viewCount || 0} views</p>
                    <p>{trend.likeCount || 0} likes</p>
                    <p>{trend.commentCount || 0} comments</p>
                  </div>
                </TableCell>
                <TableCell>
                  <p className="text-sm text-muted-foreground">
                    {fDateTime(trend.createdAt)}
                  </p>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onView(trend)}>
                        <Eye className="mr-2 h-4 w-4" />
                        ดู
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onDelete(trend)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        ลบ
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
